"use client";

import React, { useRef, useEffect, useState } from "react";
import { useRouter } from "next/navigation";
import { Worker } from "@/app/components/workercard/WorkerCard";
import { useOrdersContext } from "@/context/OrdersContext";
import {
  WorkerFrequentShopSection,
  WorkerRecentActivitySection,
  type WorkerShopPinDetailBridge,
} from "./WorkerFrequentShopSection";
import { WorkerMarketplaceSection } from "./WorkerMarketplaceSection";
import { WorkerMyTeamSection } from "./WorkerMyTeamSection";
import { Workspace } from "@/lib/api";
import {
  continuePendingBillinGo,
} from "@/lib/billinGoRoutes";
import { emitWorkspaceSelected } from "@/lib/lastWorkspaceStorage";
import { showWorkspaceSwitchToast } from "@/lib/workspaceSwitchToast";
import { BILLIN_SERVICE_ID } from "@/lib/billinConstants";
import { navigateHomeAfterWorkspacePick } from "@/lib/workspacePickHint";
import { WorkspaceSwitcher } from "@/app/components/WorkspaceSwitcher";
import {
  DEFAULT_WORKSPACE_AVATAR,
  resolveWorkspaceAvatarFromWorkspace,
  resolveWorkspaceImageDisplayUrl,
} from "@/lib/workspaceAvatar";
import { FinancialWorkPrimaryNav } from "@/app/financial-work/FinancialWorkViews";
import { ProfileMagicServiceBanner } from "@/app/components/profile-magic/ProfileMagicServiceBanner";
import type { FinancialWorkNavId } from "@/lib/financialWork/types";

export type WorkerSidebarWorkNav = {
  active: FinancialWorkNavId;
  onChange: (nav: FinancialWorkNavId) => void;
  workCount?: number | null;
  showPeriods?: boolean;
  showSources?: boolean;
};

interface WorkerSidebarProps {
  /** hire = ทีม AI / เพิ่มผู้ช่วย · work = งานเงิน / ข้อมูล */
  mode?: "hire" | "work";
  selectedWorker?: Worker | null;
  setSelectedWorker?: (
    worker: Worker,
    source?: "my-team" | "marketplace",
  ) => void;
  hiredWorkerIds?: string[];
  apiWorkers?: Worker[];
  isLoadingWorkers?: boolean;
  searchQuery?: string;
  setSearchQuery?: (query: string) => void;
  width?: number;
  selectedServiceId?: string;
  suppressLoading?: boolean;
  isWorkspaceLoading?: boolean;
  activeTab?: "my-team" | "marketplace";
  workspaces?: Workspace[];
  currentWorkspaceId?: string | null;
  isLoadingWorkspaces?: boolean;
  onOpenWorkspaceModal?: () => void;
  onAddWorkspace?: () => void;
  onWorkspaceUpdated?: () => void;
  onWorkspaceIdChange?: (workspaceId: string) => void;
  workNav?: WorkerSidebarWorkNav;
}

// Persistent scroll position across re-mounts
let sidebarScrollPos = 0;

function WorkerSidebarComponent({
  mode = "hire",
  selectedWorker = null,
  setSelectedWorker,
  hiredWorkerIds = [],
  apiWorkers: propApiWorkers,
  isLoadingWorkers: propIsLoadingWorkers,
  searchQuery = "",
  width,
  selectedServiceId = "",
  suppressLoading = false,
  isWorkspaceLoading = false,
  activeTab,
  workspaces = [],
  currentWorkspaceId = null,
  isLoadingWorkspaces = false,
  onOpenWorkspaceModal,
  onAddWorkspace,
  onWorkspaceUpdated,
  onWorkspaceIdChange,
  workNav,
}: WorkerSidebarProps) {
  const router = useRouter();
  const [isWorkspaceDropdownOpen, setIsWorkspaceDropdownOpen] = useState(false);
  const [isSidebarScrolled, setIsSidebarScrolled] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const pinDetailBridgeRef = useRef<WorkerShopPinDetailBridge | null>(null);
  const currentWorkspace = workspaces.find((w) => w.id === currentWorkspaceId);
  const workspaceForAvatar = currentWorkspace ?? workspaces[0];
  const workspaceAvatar = workspaceForAvatar
    ? resolveWorkspaceAvatarFromWorkspace({
        imageUrl: workspaceForAvatar.imageUrl,
        tags: workspaceForAvatar.tags,
        resolveImageUrl: resolveWorkspaceImageDisplayUrl,
      })
    : DEFAULT_WORKSPACE_AVATAR;
  const workspaceLabel = isLoadingWorkspaces
    ? "กำลังโหลด..."
    : (currentWorkspace?.name ?? workspaces[0]?.name ?? "Workspace");
  const { loadingOrders, workersFromOrders, loadingOrderWorkers, orders } =
    useOrdersContext();
  const isBillinHired = hiredWorkerIds.includes(BILLIN_SERVICE_ID);

  // Track previous orders count to detect new additions
  const prevOrdersCountRef = useRef<number>(0);

  const syncSidebarScrollShadow = () => {
    const el = scrollContainerRef.current;
    if (!el) return;
    setIsSidebarScrolled(el.scrollTop > 0);
  };

  // Restore scroll position on mount
  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = sidebarScrollPos;
      syncSidebarScrollShadow();
    }
  }, []);

  // Auto-scroll to My Team section when new order is added
  useEffect(() => {
    // Skip if loading or no orders
    if (loadingOrders || loadingOrderWorkers) return;

    const currentCount = workersFromOrders.length;
    const prevCount = prevOrdersCountRef.current;

    // If new order was added (count increased), scroll to top to show My Team
    if (currentCount > prevCount && prevCount > 0) {
      // Scroll to top of sidebar to show My Team section with new service
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTo({
          top: 0,
          behavior: "smooth",
        });
        setIsSidebarScrolled(false);
      }
    }

    // Update ref with current count
    prevOrdersCountRef.current = currentCount;
  }, [workersFromOrders.length, loadingOrders, loadingOrderWorkers]);

  // Helper to handle worker selection with scroll preservation
  const handleWorkerClick = (
    worker: Worker,
    source: "my-team" | "marketplace",
  ) => {
    if (scrollContainerRef.current) {
      sidebarScrollPos = scrollContainerRef.current.scrollTop;
    }
    setSelectedWorker?.(worker, source);
  };

  const isWorkMode = mode === "work";

  return (
    <aside
      style={{ width: width ? `${width}px` : undefined }}
      className={`h-full min-h-0 flex flex-col overflow-hidden shrink-0 border-r border-aera-lightgray bg-aera-white transition-all duration-300 ${!width ? "w-72" : ""} max-md:w-full!`}
    >
      <div
        className={`h-16 shrink-0 z-10 bg-aera-white border-b border-aera-lightgray transition-shadow duration-200 ${
          isSidebarScrolled
            ? "shadow-[0_4px_12px_-2px_rgba(0,0,0,0.08)]"
            : ""
        }`}
      >
        <WorkspaceSwitcher
          variant="rail"
          workspaces={workspaces}
          selectedWorkspaceId={currentWorkspaceId}
          workspaceLabel={workspaceLabel}
          workspaceAvatar={workspaceAvatar}
          isLoading={isLoadingWorkspaces}
          ready={!isLoadingWorkspaces}
          open={isWorkspaceDropdownOpen}
          onOpenChange={setIsWorkspaceDropdownOpen}
          onSelect={(workspaceId) => {
            setIsWorkspaceDropdownOpen(false);
            if (workspaceId !== currentWorkspaceId) {
              const picked = workspaces.find((w) => w.id === workspaceId);
              showWorkspaceSwitchToast(picked?.name);
            }
            emitWorkspaceSelected(workspaceId);
            onWorkspaceIdChange?.(workspaceId);
            if (
              continuePendingBillinGo(workspaceId, (href) => {
                router.replace(href, {
                  scroll: false,
                });
              })
            ) {
              return;
            }
            navigateHomeAfterWorkspacePick(
              (href) => router.replace(href, { scroll: false }),
              workspaceId,
              selectedServiceId,
              orders,
            );
          }}
          onOpenSettings={onOpenWorkspaceModal}
          onAddWorkspace={onAddWorkspace}
        />
      </div>

      {isWorkMode && workNav ? (
        <>
          <div
            ref={scrollContainerRef}
            onScroll={syncSidebarScrollShadow}
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain py-3"
          >
            <FinancialWorkPrimaryNav
              active={workNav.active}
              onChange={workNav.onChange}
              showPeriods={workNav.showPeriods}
              showSources={workNav.showSources}
              workCount={workNav.workCount}
              variant="sidebar"
            />
          </div>
          {currentWorkspaceId ? (
            <div className="shrink-0 border-t border-aera-lightgray px-4 py-2">
              <ProfileMagicServiceBanner
                workspaceId={currentWorkspaceId}
                compact
              />
            </div>
          ) : null}
        </>
      ) : (
      <div
        ref={scrollContainerRef}
        onScroll={syncSidebarScrollShadow}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain flex flex-col gap-6 px-4 py-4"
      >
        {isBillinHired ? (
          <WorkerFrequentShopSection
            workspace={currentWorkspace ?? null}
            onWorkspaceUpdated={onWorkspaceUpdated}
            pinDetailBridgeRef={pinDetailBridgeRef}
            isWorkspaceLoading={isLoadingWorkspaces}
          />
        ) : null}

        <WorkerMyTeamSection
          activeTab={activeTab}
          selectedWorker={selectedWorker}
          handleWorkerClick={handleWorkerClick}
          searchQuery={searchQuery}
          suppressLoading={suppressLoading}
          forceLoading={isWorkspaceLoading}
        />

        <WorkerMarketplaceSection
          activeTab={activeTab}
          selectedWorker={selectedWorker}
          handleWorkerClick={handleWorkerClick}
          searchQuery={searchQuery}
          hiredWorkerIds={hiredWorkerIds}
          suppressLoading={suppressLoading}
          forceLoading={isWorkspaceLoading}
          apiWorkers={propApiWorkers}
          isLoadingWorkers={isWorkspaceLoading ? true : propIsLoadingWorkers}
        />

        {isBillinHired ? (
          <WorkerRecentActivitySection workspace={currentWorkspace ?? null} />
        ) : null}
      </div>
      )}
    </aside>
  );
}

// Export memoized version to prevent unnecessary re-renders
export default React.memo(WorkerSidebarComponent);
